import { compareEventPosition } from '../../helpers/eventPosition';
import { EventLightVm, EventPeriodEnum, MatchDetailVm, MatchPlayerVm } from '../../utils/api';
import { periodCountFromSegment, periodLabel, PeriodCount } from '../../helpers/matchTiming';
import { ShotTechniqueEnum } from '../../utils/shotTechniqueEnum';

export interface PlayerMatchStats {
    matchPlayerId: number;
    capNumber: number;
    name: string;
    teamId: number;
    goals: number;
    shots: number;
    exclusions: number;
}

export const playerDisplayName = (mp: MatchPlayerVm): string => {
    const player = mp.player;
    if (!player) return `Cap ${mp.capNumber}`;
    return `${player.name || ''} ${player.surname || ''}`.trim() || `Cap ${mp.capNumber}`;
};

export const eventClock = (event: EventLightVm): string => {
    const sec = Number.isFinite(event.seconds) ? event.seconds : 0;
    return `${event.minute}:${sec.toString().padStart(2, '0')}`;
};

export const eventTeamId = (event: EventLightVm): number | undefined => {
    if (event.shot) return event.shot.teamId;
    if (event.exclusion) return event.exclusion.teamId;
    if (event.turnover) return event.turnover.teamId;
    if (event.timeout) return event.timeout.teamId;
    if (event.card) return event.card.teamId;
    if (event.swimoff) return event.swimoff.winnerTeamId;
    return undefined;
};

export const eventPrimaryMatchPlayerId = (event: EventLightVm): number | undefined => {
    if (event.shot) return event.shot.takenById;
    if (event.exclusion) return event.exclusion.excludedPlayerId;
    if (event.turnover) return event.turnover.lostPossesionPlayerId;
    if (event.card) return event.card.cardedPlayerId;
    if (event.swimoff) return event.swimoff.winnerSwimmerId;
    return undefined;
};

export const eventLabel = (event: EventLightVm): string => {
    if (event.shot) {
        if (event.shot.isGoal) return event.shot.type ? `Goal (${event.shot.type})` : 'Goal';
        return event.shot.type ? `Shot (${event.shot.type})` : 'Shot';
    }
    if (event.exclusion) return event.exclusion.type || 'Exclusion';
    if (event.turnover) return event.turnover.type || 'Turnover';
    if (event.timeout) return 'Timeout';
    if (event.card) return 'Card';
    if (event.swimoff) return 'Swim-off';
    if (event.challenge) return 'Challenge';
    if (event.goalkeeperChange) return 'GK change';
    return event.type || 'Event';
};

/** Play order: period ascending, then more time remaining first (countdown clock). */
export const sortEventsChronological = (events: EventLightVm[]): EventLightVm[] =>
    [...events].sort((a, b) => {
        if (a.periodId !== b.periodId) return a.periodId - b.periodId;
        const aRem = a.minute * 60 + (a.seconds || 0);
        const bRem = b.minute * 60 + (b.seconds || 0);
        if (aRem !== bRem) return bRem - aRem;
        return compareEventPosition(a, b);
    });

export interface TeamMatchStats {
    goals: number;
    shots: number;
    /** Opponent shots stopped by this team's goalkeeper. */
    saves: number;
    exclusions: number;
    turnovers: number;
    timeouts: number;
}

const emptyTeamStats = (): TeamMatchStats => ({
    goals: 0,
    shots: 0,
    saves: 0,
    exclusions: 0,
    turnovers: 0,
    timeouts: 0,
});

export const shootingPercent = (goals: number, shots: number): number | undefined =>
    shots > 0 ? Math.round((goals / shots) * 100) : undefined;

/**
 * Team totals for one match. Exclusions are counted for the punished team, matching how
 * the match report stores them (`exclusion.teamId` is the excluded player's team).
 */
export const buildTeamStats = (
    match: MatchDetailVm,
    events: EventLightVm[]
): { home: TeamMatchStats; away: TeamMatchStats } => {
    const home = emptyTeamStats();
    const away = emptyTeamStats();
    const sideOf = (teamId?: number): TeamMatchStats | undefined => {
        if (teamId == null) return undefined;
        if (teamId === match.homeTeamId) return home;
        if (teamId === match.awayTeamId) return away;
        return undefined;
    };

    events.forEach((event: EventLightVm) => {
        if (event.shot) {
            const attacking = sideOf(event.shot.teamId);
            if (attacking) {
                attacking.shots += 1;
                if (event.shot.isGoal) attacking.goals += 1;
            }
            if (event.shot.savedById != null) {
                const defending =
                    event.shot.teamId === match.homeTeamId
                        ? away
                        : event.shot.teamId === match.awayTeamId
                        ? home
                        : undefined;
                if (defending) defending.saves += 1;
            }
        }
        if (event.exclusion) {
            const punished = sideOf(event.exclusion.teamId);
            if (punished) punished.exclusions += 1;
        }
        if (event.turnover) {
            const losing = sideOf(event.turnover.teamId);
            if (losing) losing.turnovers += 1;
        }
        if (event.timeout) {
            const calling = sideOf(event.timeout.teamId);
            if (calling) calling.timeouts += 1;
        }
    });

    return { home, away };
};

export interface ScoreProgressPoint {
    home: number;
    away: number;
    /** Home goals minus away goals after this goal. */
    diff: number;
    label: string;
    periodId?: number;
    periodLabel?: string;
    clock?: string;
    /** True when this step is a home goal (false away, undefined for Start). */
    isHomeGoal?: boolean;
    scorerName?: string;
    assistName?: string;
    shotType?: string;
}

export const nameByMatchPlayerId = (match: MatchDetailVm): Map<number, string> => {
    const map = new Map<number, string>();
    (match.players || []).forEach((mp: MatchPlayerVm) => {
        if (mp.id == null) return;
        map.set(mp.id, playerDisplayName(mp));
    });
    return map;
};

/** Human label for shot codebook type strings (Power_Play, Regular_Attack, …). */
export const formatShotTypeLabel = (type?: string, isFastBreak?: boolean): string | undefined => {
    const key = (type || '').toLowerCase().replace(/[\s_-]/g, '');
    if (key === 'powerplay') return 'Power play';
    if (key === 'penalty') return 'Penalty';
    if (key === 'penaltyshootout') return 'Penalty shootout';
    if (key === 'regularattack' || key === 'regular') {
        return isFastBreak ? 'Fast break' : 'Regular attack';
    }
    if (!type) return isFastBreak ? 'Fast break' : undefined;
    return type.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
};

const isPenaltyShotType = (type?: string): boolean => {
    const key = (type || '').toLowerCase().replace(/[\s_-]/g, '');
    return key === 'penalty' || key === 'penaltyshootout';
};

/** Direct / Lob / Skip. Null technique is Direct, same as the match log. */
export const formatShotTechniqueLabel = (technique?: number): string => {
    if (technique === ShotTechniqueEnum.Lob) return 'Lob shot';
    if (technique === ShotTechniqueEnum.Skip) return 'Skip shot';
    return 'Direct shot';
};

/** Fakes before the shot. Hidden on penalties and when the count is 0. */
export const formatFakeCountLabel = (count?: number, shotType?: string): string | undefined => {
    if (isPenaltyShotType(shotType)) return undefined;
    const n = count ?? 0;
    if (n <= 0) return undefined;
    return n === 1 ? '1 fake' : `${n} fakes`;
};

/** Stable period keys for map filters (Q1–Q4, OT, penalty shootout). */
export type MapPeriodKey = 'q1' | 'q2' | 'q3' | 'q4' | 'ot' | 'ps';

export const MAP_PERIOD_KEYS: MapPeriodKey[] = ['q1', 'q2', 'q3', 'q4', 'ot', 'ps'];

export const MAP_PERIOD_LABELS: Record<MapPeriodKey, string> = {
    q1: 'Q1',
    q2: 'Q2',
    q3: 'Q3',
    q4: 'Q4',
    ot: 'OT',
    ps: 'Penalty shootout',
};

/** EventPeriodEnum + fixture-style 1–4 ids. */
const PERIOD_ID_TO_KEY: Record<number, MapPeriodKey> = {
    1: 'q1',
    2: 'q2',
    3: 'q3',
    4: 'q4',
    1200: 'q1',
    1201: 'q2',
    1202: 'q3',
    1203: 'q4',
    1204: 'ot',
};

/**
 * Period bucket for an event. Penalty shootout shots (type) win over OT period id -
 * same periodId as OT play in Arena.
 */
export const eventMapPeriodKey = (event: EventLightVm): MapPeriodKey | undefined => {
    const shotKey = (event.shot?.type || '').toLowerCase().replace(/[\s_-]/g, '');
    if (shotKey === 'penaltyshootout') return 'ps';

    const key = (event.period || '').toLowerCase().replace(/[\s_-]/g, '');
    if (key === 'firstperiod' || key === 'q1') return 'q1';
    if (key === 'secondperiod' || key === 'q2') return 'q2';
    if (key === 'thirdperiod' || key === 'q3') return 'q3';
    if (key === 'fourthperiod' || key === 'q4') return 'q4';
    if (key.includes('penaltyshoot') || key === 'ps' || key === 'pso') return 'ps';
    if (key.includes('overtime') || key === 'ot') return 'ot';

    if (event.periodId != null && PERIOD_ID_TO_KEY[event.periodId] != null) {
        return PERIOD_ID_TO_KEY[event.periodId];
    }
    return undefined;
};

/** null = all; [] = none; otherwise selected keys. */
export const matchesPeriodFilter = (
    event: EventLightVm,
    periodKeys?: MapPeriodKey[] | null
): boolean => {
    if (periodKeys == null) return true;
    if (periodKeys.length === 0) return false;
    const key = eventMapPeriodKey(event);
    return key != null && periodKeys.includes(key);
};

/** Human label for period enum strings (FIRST_PERIOD, …) or ids. */
export const formatPeriodLabel = (
    period?: string,
    periodId?: number,
    periodCount: PeriodCount = 4
): string => {
    const key = eventMapPeriodKey({
        period,
        periodId: periodId as EventLightVm['periodId'],
    } as EventLightVm);
    if (key === 'ps') return MAP_PERIOD_LABELS.ps;
    if (periodId != null) {
        const fromSnapshot = periodLabel(periodId as EventPeriodEnum, periodCount);
        if (fromSnapshot) return fromSnapshot;
    }
    if (key) return MAP_PERIOD_LABELS[key];
    if (period) return period.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    if (periodId != null) return `P${periodId}`;
    return '';
};

/** Running score after every goal, for the match score evolution chart. */
export const buildScoreProgress = (
    match: MatchDetailVm,
    events: EventLightVm[]
): ScoreProgressPoint[] => {
    const names = nameByMatchPlayerId(match);
    const points: ScoreProgressPoint[] = [{ home: 0, away: 0, diff: 0, label: 'Start' }];
    let home = 0;
    let away = 0;

    sortEventsChronological(events).forEach((event: EventLightVm) => {
        if (!event.shot || !event.shot.isGoal) return;
        const isHome = event.shot.teamId === match.homeTeamId;
        const isAway = event.shot.teamId === match.awayTeamId;
        if (!isHome && !isAway) return;

        if (isHome) home += 1;
        else away += 1;

        const periodLabelText = formatPeriodLabel(
            event.period,
            event.periodId,
            periodCountFromSegment(match.matchSegmentId)
        );
        const clock = eventClock(event);
        const scorerName =
            event.shot.takenById != null ? names.get(event.shot.takenById) : undefined;
        const assistName =
            event.shot.assistedById != null ? names.get(event.shot.assistedById) : undefined;

        points.push({
            home,
            away,
            diff: home - away,
            label: `${periodLabelText} ${clock} · ${home}:${away}`,
            periodId: event.periodId,
            periodLabel: periodLabelText,
            clock,
            isHomeGoal: isHome,
            scorerName,
            assistName,
            shotType: formatShotTypeLabel(event.shot.type, event.shot.isFastBreak),
        });
    });

    return points;
};

export const buildPlayerStats = (
    match: MatchDetailVm,
    events: EventLightVm[]
): PlayerMatchStats[] => {
    const byId = new Map<number, PlayerMatchStats>();

    (match.players || []).forEach((mp: MatchPlayerVm) => {
        if (mp.id == null) return;
        byId.set(mp.id, {
            matchPlayerId: mp.id,
            capNumber: mp.capNumber,
            name: playerDisplayName(mp),
            teamId: mp.teamId,
            goals: 0,
            shots: 0,
            exclusions: 0,
        });
    });

    events.forEach((event: EventLightVm) => {
        if (event.shot) {
            const row = byId.get(event.shot.takenById);
            if (row) {
                row.shots += 1;
                if (event.shot.isGoal) row.goals += 1;
            }
        }
        if (event.exclusion && event.exclusion.excludedPlayerId != null) {
            const row = byId.get(event.exclusion.excludedPlayerId);
            if (row) row.exclusions += 1;
        }
    });

    return Array.from(byId.values()).sort((a, b) => a.capNumber - b.capNumber);
};
